/**
 * @fileoverview Terms of Service page for Compass Ledger.
 * Accessible at /terms for unauthenticated and authenticated users.
 */

import styles from './LegalPage.module.css';

/**
 * TermsOfServicePage Component
 *
 * Terms of Service for Compass Ledger.
 *
 * @param {Object} props
 * @param {Function} props.onBack - Navigate back to landing page
 */
function TermsOfServicePage({ onBack }) {
    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <button onClick={onBack} className={styles.backButton}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                    Back to Home
                </button>

                <h1 className={styles.title}>Terms of Service</h1>
                <p className={styles.lastUpdated}>Effective date: March 14, 2025</p>

                <div className={styles.section}>
                    <p className={styles.text}>
                        These Terms of Service ("Terms") govern your use of Compass Ledger
                        (the "Service"), operated by Compass Ledger ("we," "us," or "our").
                        By accessing or using the Service, you agree to be bound by these Terms.
                        If you do not agree, do not use the Service.
                    </p>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>1. Access to the Service</h2>
                    <p className={styles.text}>
                        Access to Compass Ledger is currently by approval only. We reserve the
                        right to grant or deny access to any individual at our sole discretion.
                        Approved users may access the Service for personal budget tracking and
                        financial organization.
                    </p>
                    <p className={styles.text}>
                        You must be at least 13 years old to use the Service. By using the Service,
                        you represent that you meet this requirement.
                    </p>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>2. Your Account</h2>
                    <p className={styles.text}>
                        You sign in using your Google account via Auth0. You are responsible for
                        all activity that occurs under your account. If you believe your account
                        has been compromised, contact us immediately.
                    </p>
                    <p className={styles.text}>
                        You may not share your account with others, use the Service on behalf of
                        a third party without their consent, or attempt to access another user's
                        account or data.
                    </p>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>3. Your Data</h2>
                    <p className={styles.text}>
                        You own the budget and transaction data you enter into the Service. By
                        using the Service, you grant us a limited license to store, process, and
                        display that data solely for the purpose of providing the Service to you.
                    </p>
                    <p className={styles.text}>
                        You are responsible for the accuracy of the data you enter. The Service
                        is a tracking tool only — it does not validate, audit, or verify the
                        accuracy of your financial records.
                    </p>
                    <p className={styles.text}>
                        You may share a budget with other users. When you do, those users will
                        have access to view and edit the transactions and categories in that shared
                        budget. Share access only with people you trust.
                    </p>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>4. Not Financial Advice</h2>
                    <p className={styles.text}>
                        Compass Ledger is a personal budgeting tool. Nothing in the Service
                        constitutes financial, investment, tax, or legal advice. We are not a
                        financial institution, investment advisor, or tax professional.
                    </p>
                    <p className={styles.text}>
                        You are solely responsible for any financial decisions you make. Consult
                        a qualified financial professional for advice specific to your situation.
                    </p>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>5. Acceptable Use</h2>
                    <p className={styles.text}>You agree not to:</p>
                    <ul className={styles.list}>
                        <li>Use the Service for any unlawful purpose</li>
                        <li>Attempt to gain unauthorized access to the Service or its infrastructure</li>
                        <li>Introduce malware, viruses, or other harmful code</li>
                        <li>Scrape, crawl, or systematically extract data from the Service</li>
                        <li>Interfere with or disrupt the Service or servers connected to it</li>
                        <li>Impersonate any person or entity</li>
                    </ul>
                    <p className={styles.text}>
                        We reserve the right to suspend or terminate your account for violations
                        of these terms without notice.
                    </p>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>6. Service Availability</h2>
                    <p className={styles.text}>
                        We strive to keep the Service available, but we do not guarantee
                        uninterrupted uptime. The Service may be unavailable due to maintenance,
                        outages, or circumstances beyond our control. We are not liable for any
                        loss or inconvenience caused by downtime.
                    </p>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>7. Disclaimer of Warranties</h2>
                    <p className={styles.text}>
                        THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF
                        ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO
                        WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR
                        NON-INFRINGEMENT.
                    </p>
                    <p className={styles.text}>
                        We do not warrant that the Service will be error-free, that defects will
                        be corrected, or that the Service or its servers are free of viruses or
                        other harmful components.
                    </p>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>8. Limitation of Liability</h2>
                    <p className={styles.text}>
                        TO THE MAXIMUM EXTENT PERMITTED BY LAW, COMPASS LEDGER SHALL NOT BE
                        LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
                        DAMAGES, INCLUDING LOSS OF DATA OR FINANCIAL LOSS, ARISING OUT OF OR
                        RELATED TO YOUR USE OF THE SERVICE, EVEN IF WE HAVE BEEN ADVISED OF THE
                        POSSIBILITY OF SUCH DAMAGES.
                    </p>
                    <p className={styles.text}>
                        OUR TOTAL LIABILITY TO YOU FOR ANY CLAIMS ARISING FROM USE OF THE SERVICE
                        SHALL NOT EXCEED FIFTY DOLLARS ($50.00).
                    </p>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>9. Termination</h2>
                    <p className={styles.text}>
                        We may suspend or terminate your access to the Service at any time, for
                        any reason, with or without notice. You may stop using the Service at
                        any time. To request deletion of your account and data, contact us at
                        the email below.
                    </p>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>10. Changes to These Terms</h2>
                    <p className={styles.text}>
                        We may update these Terms from time to time. We will update the effective
                        date at the top of this page when we do. Continued use of the Service
                        after changes are posted constitutes your acceptance of the updated Terms.
                    </p>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>11. Governing Law</h2>
                    <p className={styles.text}>
                        These Terms are governed by the laws of the State of Utah and applicable
                        US federal law. Any disputes will be resolved in the courts of Utah.
                    </p>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>12. Contact Us</h2>
                    <p className={styles.text}>
                        If you have questions about these Terms, please contact us at:
                    </p>
                    <p className={styles.contactEmail}>
                        compassledger.app@gmail.com
                    </p>
                </div>
            </div>
        </div>
    );
}

export default TermsOfServicePage;
